// An exploration's seed (docs/exploration-mode.md X24 … X26).
//
// A seed streams into the page as a reply does. As it comes in, its heading
// takes the focus, so the reader lands on it, unless they are typing
// somewhere else meanwhile.
//
// What is in the seed's box is kept in this browser when a new seed is asked
// for over edits to the old one, and when the seed is confirmed. It comes
// back read-only, to copy, where the page has a place for it: under the new
// seed, or on the page a confirm lands on when someone else closed the
// exploration first. The editor a confirmed seed opens says so, and the kept
// seed is forgotten then.
//
// A seed box a refused Confirm brought back holds the seed as it was sent
// (X25); it is marked typed, so the page bringing itself up to date after
// the model finishes (live.ts) doesn't put the stored seed over it. The
// refusal's notice names the box (data-kept-box): the box itself is served
// as the exploration's own address serves it, so live.ts brings only its
// text up to date, which a box marked typed doesn't show.
//
// Beside the seed's box the page warns when its Design notes and Rejected
// lines come to more than the 2,000 characters design notes hold, so the
// lines past the limit would be left out of the spec's (X23, X28). The
// server says it for the seed it serves; this says it anew as the seed is
// edited, counting as `design_notes::seed_notes_chars` does, and the
// announcer says it once as the seed goes past the limit. A page bringing
// itself up to date (live.ts) serves the warning for the stored seed, while
// the box may hold other text (edits, or a refused Confirm's seed), so the
// warning is said anew from the box whenever the seed's place changes.

import { announce } from "./announce";

declare const htmx: {
  on: (event: string, handler: (evt: CustomEvent) => void) => void;
};

/** The seed's box on the exploration page. */
const SEED_BOX = "seed-text";

/** Where a seed waits, per exploration address. */
const keptKey = (explore: string) => `specter-seed:${explore}`;

/**
 * The exploration a seed form belongs to: its action less the last step,
 * `/acme/shop/12/explore/seed` or `…/confirm`.
 */
export function exploreOf(action: string): string | null {
  const path = new URL(action, window.location.href).pathname;
  const match = path.match(/^(.*\/explore)\/(seed|confirm)$/);
  return match?.[1] ?? null;
}

function keep(explore: string, text: string): void {
  try {
    sessionStorage.setItem(keptKey(explore), text);
  } catch {
    // No storage here: nothing is kept, as before.
  }
}

function forget(explore: string): void {
  try {
    sessionStorage.removeItem(keptKey(explore));
  } catch {
    // Nothing was kept.
  }
}

/** The seed's box, if this page has one. */
function seedBox(): HTMLTextAreaElement | null {
  const box = document.getElementById(SEED_BOX);
  return box instanceof HTMLTextAreaElement ? box : null;
}

/**
 * Keep what the seed's box holds as its forms go: before a new seed, only
 * what was typed into it; on Confirm, all of it, for the page it lands on.
 */
function sending(event: Event): void {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  const explore = exploreOf(form.action);
  const box = seedBox();
  if (!explore || !box) return;
  const confirming = form.action.endsWith("/confirm");
  if (confirming || box.value !== box.defaultValue) keep(explore, box.value);
}

/**
 * Show a kept seed in each place the page has for one, once: a seed the
 * box already holds isn't shown again.
 */
function showKept(): void {
  for (const place of document.querySelectorAll<HTMLElement>(
    "[data-kept-seed]",
  )) {
    if (!place.hidden) continue;
    const explore = place.dataset.keptSeed ?? "";
    let text: string | null;
    try {
      text = sessionStorage.getItem(keptKey(explore));
    } catch {
      return;
    }
    if (!text || seedBox()?.value === text) continue;
    const field = place.querySelector("textarea");
    if (!(field instanceof HTMLTextAreaElement)) continue;
    field.value = text;
    place.hidden = false;
    forget(explore);
  }
}

/** Forget the seed of each exploration the page says was posted. */
function forgetSent(): void {
  for (const el of document.querySelectorAll<HTMLElement>("[data-seed-sent]")) {
    forget(el.dataset.seedSent ?? "");
  }
}

/** Move the focus to a seed that just came in, unless it is busy elsewhere. */
function focusArrived(): void {
  const heading = document.querySelector<HTMLElement>("[data-arrived]");
  if (!heading) return;
  heading.removeAttribute("data-arrived");
  const active = document.activeElement;
  if (active === null || active === document.body) heading.focus();
}

/**
 * Mark the seed a refused Confirm brought back as typed: setting a box's
 * value makes its text no longer follow what it is served with.
 */
function holdKept(): void {
  const named = document.querySelector<HTMLElement>("[data-kept-box]");
  if (named?.dataset.keptBox !== SEED_BOX) return;
  const box = seedBox();
  if (!box) return;
  const sent = box.value;
  box.value = sent;
}

/** Where the warning about the seed's design notes goes (X23). */
const NOTES_OVER = "seed-notes-over";

/** The seed's place on the exploration page, which the seed review is in. */
const SEED_DRAFT = "seed-draft";

/**
 * The lines of the seed's section `name` (matched without case), as
 * `section_lines` in `design_notes.rs` reads them: every line after its
 * heading and before the next, outside fenced blocks, right-trimmed, less
 * the blank lines around the headings.
 */
function sectionLines(seed: string, name: string): string[] {
  const out: string[] = [];
  let inside = false;
  let fence = false;
  const lines = seed.split("\n");
  if (lines.at(-1) === "") lines.pop();
  for (const raw of lines) {
    const line = raw.endsWith("\r") ? raw.slice(0, -1) : raw;
    const trimmed = line.trim();
    if (trimmed.startsWith("```")) fence = !fence;
    if (!fence) {
      const rest = trimmed.replace(/^#+/, "");
      if (rest.length !== trimmed.length && rest.startsWith(" ")) {
        const heading = rest.trim().replace(/:+$/, "").trim();
        inside = heading.toLowerCase() === name;
        continue;
      }
    }
    if (inside && !(out.length === 0 && trimmed === "")) {
      out.push(line.trimEnd());
    }
  }
  while (out.at(-1) === "") out.pop();
  return out;
}

/**
 * How many characters the seed's Design notes and Rejected lines come to as
 * the spec's design notes, the blank line, the `Rejected:` label and line
 * breaks counted (X28): `design_notes::seed_notes_chars`.
 */
export function seedNotesChars(seed: string): number {
  const lines = sectionLines(seed, "design notes");
  const rejected = sectionLines(seed, "rejected");
  if (rejected.length > 0) {
    if (lines.length > 0) lines.push("");
    lines.push("Rejected:", ...rejected);
  }
  // Characters, not UTF-16 units, as the server counts them.
  return [...lines.join("\n")].length;
}

/** Whether the warning said the seed was over the limit when last drawn. */
let wasOver: boolean | null = null;

/** Say beside the seed's box whether its design notes run past the limit. */
function warnOver(): void {
  const box = seedBox();
  const line = document.getElementById(NOTES_OVER);
  if (!box || !line) return;
  const limit = Number(line.dataset.limit ?? "2000");
  const n = seedNotesChars(box.value);
  const over = n > limit;
  const words = over
    ? (line.dataset.over ?? "").replace("{n}", n.toLocaleString("en-US"))
    : "";
  if (line.textContent !== words) line.textContent = words;
  // Said once as it goes past, not at every key while it stays there.
  if (over && wasOver === false) announce(words);
  wasOver = over;
}

export function installSeedHooks(): void {
  document.addEventListener("submit", sending, true);
  htmx.on("htmx:after:settle", () => {
    focusArrived();
    showKept();
    warnOver();
  });
  document.addEventListener("input", (event) => {
    if (event.target instanceof Element && event.target.id === SEED_BOX) {
      warnOver();
    }
  });
  holdKept();
  forgetSent();
  showKept();
  warnOver();
  const draft = document.getElementById(SEED_DRAFT);
  if (draft) {
    new MutationObserver(() => warnOver()).observe(draft, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }
}

installSeedHooks();
